import React, { useMemo, useState } from 'react';
import {
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
  List,
  ListItemButton,
  Checkbox,
  CircularProgress,
  Box,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Add, Close, Search, Link as LinkIcon } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useQueryClient } from '@tanstack/react-query';
import { useCollections } from '../hooks/useCollections';
import { useToast } from '@shared/hooks/useToast';
import { Collection } from '@domain/product/models/Collection';

export interface LinkProductToCollectionsSheetProps {
  productId: string;
  /** Collections this product is already in — shown disabled. */
  existingCollectionIds?: Set<string>;
  onClose: () => void;
  /** After closing this sheet, opens the create-collection flow (product picker → name). */
  onCreateNewCollection?: () => void;
}

const LinkProductToCollectionsSheet: React.FC<LinkProductToCollectionsSheetProps> = ({
  productId,
  existingCollectionIds = new Set(),
  onClose,
  onCreateNewCollection,
}) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const { collections: collectionsResponse, isLoading, addProductsToCollection } = useCollections();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const collections =
    collectionsResponse?.status === 'success' ? collectionsResponse.data : [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return collections.filter(c => !term || c.name.toLowerCase().includes(term));
  }, [collections, search]);

  const toggle = (collection: Collection) => {
    if (existingCollectionIds.has(collection.id)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(collection.id)) next.delete(collection.id);
      else next.add(collection.id);
      return next;
    });
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearch('');
    onClose();
  };

  const handleCreateNewCollection = () => {
    onClose();
    onCreateNewCollection?.();
  };

  const handleLink = async () => {
    if (selectedIds.size === 0) return;
    setIsSubmitting(true);
    try {
      for (const collectionId of selectedIds) {
        await addProductsToCollection({ collectionId, productIds: [productId] });
      }
      success(
        selectedIds.size === 1
          ? 'Product linked to collection'
          : `Product linked to ${selectedIds.size} collections`
      );
      await queryClient.invalidateQueries({ queryKey: ['product', productId] });
      handleClose();
    } catch {
      error('Failed to link product to collection');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">Add to collection</Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 0 }}>
        <Stack spacing={2} sx={{ p: 3, pt: 2 }}>
          <TextField
            placeholder="Search collections..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="body2" color="text.secondary">
            Select collections and tap Link, or create a new collection below.
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {search.trim() ? 'No collections match your search.' : 'No collections yet. Create one first.'}
            </Typography>
          ) : (
            <List
              dense
              disablePadding
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {filtered.map(collection => {
                const alreadyIn = existingCollectionIds.has(collection.id);
                const checked = alreadyIn || selectedIds.has(collection.id);
                return (
                  <ListItemButton
                    key={collection.id}
                    disabled={alreadyIn}
                    selected={selectedIds.has(collection.id)}
                    onClick={() => toggle(collection)}
                    sx={{
                      opacity: alreadyIn ? 0.55 : 1,
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={checked}
                      disabled={alreadyIn}
                      tabIndex={-1}
                      disableRipple
                      onClick={e => {
                        e.stopPropagation();
                        if (!alreadyIn) toggle(collection);
                      }}
                    />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {collection.name}
                      </Typography>
                      {alreadyIn ? (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                          Already added
                        </Typography>
                      ) : null}
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          )}

          {onCreateNewCollection ? (
            <>
              <Divider />
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                onClick={handleCreateNewCollection}
                sx={{ justifyContent: 'flex-start', py: 1.25 }}
              >
                Create new collection
              </Button>
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2, justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isSubmitting ? undefined : <LinkIcon />}
          onClick={() => void handleLink()}
          disabled={selectedIds.size === 0 || isSubmitting || isLoading}
        >
          {isSubmitting ? (
            <Stack direction="row" alignItems="center" gap={1}>
              <CircularProgress size={18} color="inherit" />
              Linking…
            </Stack>
          ) : (
            `Link${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`
          )}
        </Button>
      </DialogActions>
    </>
  );
};

export default LinkProductToCollectionsSheet;
